// src/pages/Officer/StrategyDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
  alpha,
  Divider,
  Grid,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Schedule,
  Cancel,
  TrendingUp,
  ShowChart,
  Insights,
  Timeline,
  Circle,
  CallReceived,
  FiberManualRecord,
} from '@mui/icons-material';
import { strategyAPI } from '../../services/api';

/**
 * StrategyDetail - Comprehensive strategy drill-down page
 */
const StrategyDetail = () => {
  const { strategyId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [strategyData, setStrategyData] = useState(null);

  useEffect(() => {
    fetchStrategyDetail();
  }, [strategyId]);

  const fetchStrategyDetail = async () => {
    try {
      setLoading(true);
      const result = await strategyAPI.getStrategyDetail(strategyId);
      
      if (result.success) {
        setStrategyData(result.data);
      } else {
        setError(result.message || 'Failed to load strategy details');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#0a0e14',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  if (error || !strategyData) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#0a0e14',
          color: 'grey.100',
        }}
      >
        <Typography variant="h6" sx={{ color: '#ff4444', mb: 2 }}>
          {error || 'Strategy not found'}
        </Typography>
        <IconButton onClick={() => navigate(-1)} sx={{ color: '#00d4ff' }}>
          <ArrowBack />
        </IconButton>
      </Box>
    );
  }

  const { strategy, condition_states, entry_summary, recent_signals, detected_signals } = strategyData;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0a0e14',
        color: 'grey.100',
        pb: 4,
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: alpha('#0f1419', 0.95),
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: alpha('#00d4ff', 0.1),
        }}
      >
        <Container maxWidth="xl" sx={{ py: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                bgcolor: alpha('#00d4ff', 0.1),
                '&:hover': { bgcolor: alpha('#00d4ff', 0.2) },
              }}
            >
              <ArrowBack sx={{ color: '#00d4ff' }} />
            </IconButton>
            
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'grey.50' }}>
                  {strategy.strategy_id}
                </Typography>
                <Chip
                  label={strategy.is_active ? 'ACTIVE' : 'INACTIVE'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: strategy.is_active
                      ? alpha('#00ff88', 0.15)
                      : alpha('#666', 0.15),
                    color: strategy.is_active ? '#00ff88' : '#9e9e9e',
                    border: '1px solid',
                    borderColor: strategy.is_active
                      ? alpha('#00ff88', 0.3)
                      : alpha('#666', 0.3),
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'grey.600' }}>
                  {strategy.symbol}
                </Typography>
                <Circle sx={{ fontSize: 4, color: 'grey.800' }} />
                <Typography variant="caption" sx={{ color: 'grey.600' }}>
                  {strategy.timeframe}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Container>
      </Paper>

      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Grid container spacing={3}>
          {/* Entry Requirements Summary */}
          <Grid item xs={12}>
            <EntryRequirementsCard entrySummary={entry_summary} strategy={strategy} />
          </Grid>

          {/* Condition Status Board */}
          <Grid item xs={12}>
            <ConditionStatusBoard
              conditions={strategy.conditions}
              conditionStates={condition_states}
              detectedSignals={detected_signals}
            />
          </Grid>

          {/* Signal Activity Timeline */}
          <Grid item xs={12} lg={6}>
            <SignalActivityCard recentSignals={recent_signals} />
          </Grid>

          {/* Detected Conditions */}
          <Grid item xs={12} lg={6}>
            <DetectedConditionsCard detectedSignals={detected_signals} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

/**
 * EntryRequirementsCard - Trader-friendly entry requirements
 */
const EntryRequirementsCard = ({ entrySummary, strategy }) => {
  const progress = (entrySummary.fulfilled_count / entrySummary.total_conditions) * 100;

  return (
    <Card
      sx={{
        bgcolor: alpha('#0f1419', 0.8),
        border: '2px solid',
        borderColor: entrySummary.entry_triggered
          ? alpha('#00ff88', 0.5)
          : alpha('#00d4ff', 0.3),
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  bgcolor: entrySummary.entry_triggered
                    ? alpha('#00ff88', 0.15)
                    : alpha('#00d4ff', 0.15),
                  border: '1px solid',
                  borderColor: entrySummary.entry_triggered
                    ? alpha('#00ff88', 0.3)
                    : alpha('#00d4ff', 0.3),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {entrySummary.entry_triggered ? (
                  <CheckCircle sx={{ color: '#00ff88', fontSize: 28 }} />
                ) : (
                  <TrendingUp sx={{ color: '#00d4ff', fontSize: 28 }} />
                )}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'grey.50' }}>
                  {entrySummary.entry_triggered ? 'ENTRY TRIGGERED!' : 'Entry Requirements'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'grey.500' }}>
                  {entrySummary.entry_logic === 'all'
                    ? 'All conditions must be met'
                    : 'Any condition can trigger entry'}
                </Typography>
              </Box>
            </Stack>

            <Chip
              label={`${entrySummary.fulfilled_count}/${entrySummary.total_conditions} MET`}
              sx={{
                height: 32,
                fontSize: '0.9rem',
                fontWeight: 700,
                bgcolor: alpha('#00d4ff', 0.15),
                color: '#00d4ff',
                border: '1px solid',
                borderColor: alpha('#00d4ff', 0.3),
              }}
            />
          </Stack>

          {/* Progress Bar */}
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'grey.600', fontWeight: 600 }}>
                COMPLETION
              </Typography>
              <Typography variant="caption" sx={{ color: '#00d4ff', fontWeight: 700 }}>
                {Math.round(progress)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: alpha('#00d4ff', 0.1),
                '& .MuiLinearProgress-bar': {
                  bgcolor: entrySummary.ready_for_entry ? '#00ff88' : '#00d4ff',
                  borderRadius: 1,
                },
              }}
            />
          </Box>

          {/* Status Summary */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <StatusSummaryBox
                icon={<CheckCircle />}
                label="Fulfilled"
                count={entrySummary.fulfilled_count}
                color="#00ff88"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatusSummaryBox
                icon={<Schedule />}
                label="Waiting"
                count={entrySummary.waiting_count}
                color="#ffaa00"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatusSummaryBox
                icon={<Cancel />}
                label="Pending"
                count={entrySummary.pending_count}
                color="#9e9e9e"
              />
            </Grid>
          </Grid>

          {/* Entry Message */}
          {entrySummary.entry_triggered && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: alpha('#00ff88', 0.1),
                border: '1px solid',
                borderColor: alpha('#00ff88', 0.3),
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#00ff88', fontWeight: 700, textAlign: 'center' }}
              >
                ✓ All entry conditions satisfied - Ready to execute trade
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

/**
 * StatusSummaryBox
 */
const StatusSummaryBox = ({ icon, label, count, color }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 1.5,
      bgcolor: alpha(color, 0.1),
      border: '1px solid',
      borderColor: alpha(color, 0.2),
      textAlign: 'center',
    }}
  >
    <Box sx={{ color, mb: 1 }}>{icon}</Box>
    <Typography variant="h4" sx={{ fontWeight: 700, color, mb: 0.5 }}>
      {count}
    </Typography>
    <Typography variant="caption" sx={{ color: 'grey.600', fontWeight: 600 }}>
      {label}
    </Typography>
  </Box>
);

/**
 * ConditionStatusBoard - Visual cards for each condition
 */
const ConditionStatusBoard = ({ conditions, conditionStates, detectedSignals }) => {
  return (
    <Card
      sx={{
        bgcolor: alpha('#0f1419', 0.8),
        border: '1px solid',
        borderColor: alpha('#666', 0.2),
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'grey.50', mb: 3 }}>
          Condition Status
        </Typography>

        <Grid container spacing={2}>
          {conditions.map((condition, index) => {
            if (!condition.enabled) return null;

            const condName = condition.name || condition.signal_name;
            const state = conditionStates[condName]?.state || {};
            const isMet = detectedSignals.some(
              ds => ds.condition_name === condName && !ds.is_entry_signal
            );

            return (
              <Grid item xs={12} md={6} lg={4} key={condName}>
                <ConditionCard
                  condition={condition}
                  state={state}
                  isMet={isMet}
                  index={index + 1}
                />
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

/**
 * ConditionCard
 */
const ConditionCard = ({ condition, state, isMet, index }) => {
  const condName = condition.name || condition.signal_name;
  const condType = condition.type;

  const getStatus = () => {
    if (isMet || state.was_met || state.condition_met) {
      return { label: 'FULFILLED', color: '#00ff88', icon: <CheckCircle /> };
    }
    if (state.waiting) {
      return { label: 'WAITING', color: '#ffaa00', icon: <Schedule /> };
    }
    return { label: 'PENDING', color: '#9e9e9e', icon: <Cancel /> };
  };

  const status = getStatus();

  // Build readable description
  const getDescription = () => {
    if (condType === 'simultaneous') {
      const sig1 = condition.signal1;
      const sig2 = condition.signal2;
      return `${sig1.algo} ${sig1.direction} + ${sig2.algo} ${sig2.direction} must fire together`;
    }
    if (condType === 'time_bound') {
      const trigger = condition.trigger;
      const target = condition.target;
      return `${trigger.algo} ${trigger.direction} → ${target.algo} ${target.direction} (${condition.min_minutes}-${condition.max_minutes} min)`;
    }
    return condType;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: alpha('#000', 0.3),
        border: '2px solid',
        borderColor: alpha(status.color, 0.3),
        borderRadius: 2,
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Chip
            label={`#${index}`}
            size="small"
            sx={{
              width: 36,
              height: 24,
              fontWeight: 700,
              bgcolor: alpha('#00d4ff', 0.15),
              color: '#00d4ff',
            }}
          />
          <Chip
            icon={status.icon}
            label={status.label}
            size="small"
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 700,
              bgcolor: alpha(status.color, 0.15),
              color: status.color,
              border: '1px solid',
              borderColor: alpha(status.color, 0.3),
              '& .MuiChip-icon': {
                color: status.color,
              },
            }}
          />
        </Stack>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'grey.100', mb: 0.5 }}>
            {condName}
          </Typography>
          <Typography variant="caption" sx={{ color: 'grey.600', lineHeight: 1.4 }}>
            {getDescription()}
          </Typography>
        </Box>

        {state.waiting && (
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: alpha('#ffaa00', 0.1),
              border: '1px solid',
              borderColor: alpha('#ffaa00', 0.2),
            }}
          >
            <Typography variant="caption" sx={{ color: '#ffaa00', fontWeight: 600 }}>
              ⏱ Trigger activated - waiting for target signal
            </Typography>
          </Box>
        )}

        {isMet && (
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: alpha('#00ff88', 0.1),
              border: '1px solid',
              borderColor: alpha('#00ff88', 0.2),
            }}
          >
            <Typography variant="caption" sx={{ color: '#00ff88', fontWeight: 600 }}>
              ✓ Condition satisfied
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

/**
 * SignalActivityCard
 */
const SignalActivityCard = ({ recentSignals }) => {
  return (
    <Card
      sx={{
        bgcolor: alpha('#0f1419', 0.8),
        border: '1px solid',
        borderColor: alpha('#666', 0.2),
        borderRadius: 2,
        height: 500,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: 2.5, pb: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <CallReceived sx={{ color: '#00d4ff', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'grey.100' }}>
            Recent Signals ({recentSignals.length})
          </Typography>
        </Stack>
      </CardContent>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 2.5,
          pb: 2.5,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: alpha('#00d4ff', 0.3),
            borderRadius: 1,
          },
        }}
      >
        {recentSignals.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: 'grey.600' }}>
              No signals received yet
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {recentSignals.map((signal) => (
              <Paper
                key={signal.id}
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor: alpha('#000', 0.3),
                  border: '1px solid',
                  borderColor: alpha('#00d4ff', 0.2),
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FiberManualRecord sx={{ fontSize: 8, color: '#00d4ff' }} />
                    <Typography
                      variant="body2"
                      sx={{ color: '#00d4ff', fontFamily: 'monospace', fontSize: '0.85rem' }}
                    >
                      {signal.signal_key}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{ color: 'grey.600', fontFamily: 'monospace' }}
                  >
                    {new Date(signal.received_at).toLocaleTimeString()}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </Card>
  );
};

/**
 * DetectedConditionsCard
 */
const DetectedConditionsCard = ({ detectedSignals }) => {
  const nonEntrySignals = detectedSignals.filter(ds => !ds.is_entry_signal);

  return (
    <Card
      sx={{
        bgcolor: alpha('#0f1419', 0.8),
        border: '1px solid',
        borderColor: alpha('#666', 0.2),
        borderRadius: 2,
        height: 500,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: 2.5, pb: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <CheckCircle sx={{ color: '#00ff88', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'grey.100' }}>
            Conditions Met ({nonEntrySignals.length})
          </Typography>
        </Stack>
      </CardContent>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 2.5,
          pb: 2.5,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: alpha('#00ff88', 0.3),
            borderRadius: 1,
          },
        }}
      >
        {nonEntrySignals.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: 'grey.600' }}>
              No conditions met yet
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {nonEntrySignals.map((signal) => (
              <Paper
                key={signal.id}
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor: alpha('#00ff88', 0.05),
                  border: '1px solid',
                  borderColor: alpha('#00ff88', 0.2),
                  borderRadius: 1,
                }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="body2"
                      sx={{ color: '#00ff88', fontWeight: 600 }}
                    >
                      {signal.condition_name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'grey.600', fontFamily: 'monospace' }}
                    >
                      {new Date(signal.detected_at).toLocaleTimeString()}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: 'grey.600' }}>
                    {signal.condition_type}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </Card>
  );
};

export default StrategyDetail;
